import { Group, Person, Security } from "@mui/icons-material";
import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useMemo } from "react";

import { useGetMeQuery } from "../../../../store/api/usersApi";
import { useMyMemberships } from "../../../../store/hooks/useMyMemberships";
import type { SelectedTarget, TargetType } from "../data/types";

type SelectableTarget = {
  id: string;
  name: string;
};

type TargetTypeOption = {
  value: TargetType;
  label: string;
  icon: React.ElementType;
};

const TARGET_TYPE_OPTIONS: TargetTypeOption[] = [
  { value: "user", label: "User", icon: Person },
  { value: "role", label: "Role", icon: Security },
  { value: "team", label: "Team", icon: Group },
];

type RecipientSelectionFieldProps = {
  targetType: TargetType;
  value: SelectedTarget | null;
  onTargetTypeChange: (targetType: TargetType) => void;
  onChange: (target: SelectedTarget | null) => void;
};

function RecipientSelectionField({
  targetType,
  value,
  onTargetTypeChange,
  onChange,
}: RecipientSelectionFieldProps) {
  const { data: currentUser, isLoading: isLoadingUser } = useGetMeQuery();
  const {
    roles: myRoles,
    teams: myTeams,
    isLoading: isLoadingMemberships,
  } = useMyMemberships(currentUser?.id);

  const targetTypeOption =
    TARGET_TYPE_OPTIONS.find((opt) => opt.value === targetType) ??
    TARGET_TYPE_OPTIONS[0];

  const targetOptions: SelectableTarget[] = useMemo(() => {
    if (targetType === "user") {
      if (!currentUser) return [];
      return [
        {
          id: currentUser.id,
          name: currentUser.fullName || currentUser.name || currentUser.id,
        },
      ];
    }
    const membershipsOfSelectedType = targetType === "role" ? myRoles : myTeams;
    return membershipsOfSelectedType.map((resource) => ({
      id: resource.id,
      name: resource.fullName ?? resource.name ?? resource.id,
    }));
  }, [targetType, currentUser, myRoles, myTeams]);

  const handleTargetTypeChange = (
    _event: React.SyntheticEvent,
    newValue: TargetTypeOption | null,
  ) => {
    if (newValue) {
      onTargetTypeChange(newValue.value);
    }
  };

  const handleTargetChange = (
    _event: React.SyntheticEvent,
    newValue: SelectableTarget | null,
  ) => {
    onChange(newValue);
  };

  const isLoadingTargets =
    targetType === "user" ? isLoadingUser : isLoadingMemberships;

  // Auto-select when there's only one option available
  useEffect(() => {
    if (targetOptions.length === 1 && value?.id !== targetOptions[0].id) {
      onChange(targetOptions[0]);
    }
  }, [targetOptions, value?.id, onChange]);

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Autocomplete
        sx={{ width: 200 }}
        options={TARGET_TYPE_OPTIONS}
        value={targetTypeOption}
        onChange={handleTargetTypeChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        disableClearable
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          const Icon = option.icon;
          return (
            <li key={key} {...rest}>
              <Icon fontSize="small" sx={{ mr: 1 }} />
              {option.label}
            </li>
          );
        }}
        renderInput={(params) => {
          const Icon = targetTypeOption.icon;
          return (
            <TextField
              {...params}
              label="Request for"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          );
        }}
      />

      <Autocomplete
        sx={{ flex: 1 }}
        options={targetOptions}
        value={targetOptions.find((o) => o.id === value?.id) ?? null}
        onChange={handleTargetChange}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        loading={isLoadingTargets}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`Select ${targetTypeOption.label.toLowerCase()}`}
            placeholder={`Choose a ${targetTypeOption.label.toLowerCase()}...`}
            variant="outlined"
          />
        )}
        noOptionsText={`No ${targetTypeOption.label.toLowerCase()}s available`}
      />
    </Box>
  );
}

export default RecipientSelectionField;
